/**
 * Engine de auto tracking configurável (Bloco 5).
 * Suporta data-track, textos esperados e URL patterns (ex.: thank-you, payment success).
 */

import type { TrackConversionFn } from './engine';
import type { ConversionEventType } from './engine';

export interface AutoTrackSelectorRule {
  /** Seletor CSS (ex.: [data-track], [data-track="purchase"]). */
  selector: string;
  /** Tipo de evento de conversão a disparar. */
  eventType: ConversionEventType;
  /** Atributo opcional para extrair dados (ex.: data-track-value). */
  dataAttribute?: string;
}

export interface AutoTrackTextRule {
  /** Texto ou regex para match em botões/links. */
  text: string | RegExp;
  /** Tipo de evento a disparar. */
  eventType: ConversionEventType;
}

export interface AutoTrackUrlRule {
  /** Padrão de URL (string ou RegExp). Ex.: /thank-you|obrigado/ ou "thank-you". */
  pattern: string | RegExp;
  /** Tipo de evento (ex.: purchase para página de obrigado). */
  eventType: ConversionEventType;
}

export interface AutoTrackConfig {
  selectors?: AutoTrackSelectorRule[];
  texts?: AutoTrackTextRule[];
  urlPatterns?: AutoTrackUrlRule[];
}

let trackFn: TrackConversionFn | null = null;
let selectorUninstall: (() => void) | null = null;
let urlUninstall: (() => void) | null = null;

function getUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

function getPath(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

function installSelectorListeners(config: AutoTrackSelectorRule[]): void {
  if (typeof document === 'undefined' || !trackFn) return;
  const handler = (e: Event): void => {
    let el: Element | null = (e.target as Element) ?? null;
    while (el && el !== document.body) {
      const rule = config.find(r => el!.matches(r.selector));
      if (rule) {
        const data: Record<string, unknown> = {};
        if (rule.dataAttribute) {
          const val = el.getAttribute(rule.dataAttribute);
          if (val) data.value = val;
        }
        trackFn!(rule.eventType, data);
        return;
      }
      el = el.parentElement;
    }
  };
  document.addEventListener('click', handler, true);
  selectorUninstall = () => {
    document.removeEventListener('click', handler, true);
    selectorUninstall = null;
  };
}

function installUrlListeners(rules: AutoTrackUrlRule[]): void {
  if (typeof window === 'undefined' || !trackFn) return;
  const check = (): void => {
    const url = getUrl();
    const path = getPath();
    for (const rule of rules) {
      const pattern =
        typeof rule.pattern === 'string'
          ? new RegExp(rule.pattern, 'i')
          : rule.pattern;
      if (pattern.test(url) || pattern.test(path)) {
        trackFn!(rule.eventType, {});
        break;
      }
    }
  };
  const onPopState = (): void => check();
  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);
  history.pushState = function (
    state: unknown,
    title: string,
    url?: string | URL | null
  ) {
    origPush(state, title, url);
    check();
  };
  history.replaceState = function (
    state: unknown,
    title: string,
    url?: string | URL | null
  ) {
    origReplace(state, title, url);
    check();
  };
  window.addEventListener('popstate', onPopState);
  urlUninstall = () => {
    window.removeEventListener('popstate', onPopState);
    history.pushState = origPush;
    history.replaceState = origReplace;
    urlUninstall = null;
  };
  check();
}

/**
 * Inicializa o engine de auto track com config (selectors, texts, urlPatterns).
 * Deve ser chamado após initConversionEngine; recebe getConversionTrack().
 */
export function initAutoTrackEngine(
  config: AutoTrackConfig,
  getTrack: () => TrackConversionFn
): void {
  destroyAutoTrackEngine();
  trackFn = getTrack();
  if (config.selectors?.length) {
    installSelectorListeners(config.selectors);
  }
  if (config.urlPatterns?.length) {
    installUrlListeners(config.urlPatterns);
  }
  if (config.texts?.length && typeof document !== 'undefined') {
    const textHandler = (e: Event): void => {
      const el = (e.target as HTMLElement)?.closest?.(
        'a, button, [role="button"]'
      );
      if (!el?.textContent) return;
      const text = el.textContent.trim();
      for (const rule of config.texts!) {
        const match =
          typeof rule.text === 'string'
            ? text.includes(rule.text)
            : rule.text.test(text);
        if (match) {
          trackFn?.(rule.eventType, {});
          return;
        }
      }
    };
    document.addEventListener('click', textHandler, true);
    const prevUninstall = selectorUninstall;
    selectorUninstall = () => {
      document.removeEventListener('click', textHandler, true);
      prevUninstall?.();
      selectorUninstall = null;
    };
  }
}

/**
 * Desliga listeners do auto track engine.
 */
export function destroyAutoTrackEngine(): void {
  selectorUninstall?.();
  urlUninstall?.();
  trackFn = null;
}
